import React, { useEffect, useState, useMemo } from "react";
import {
  PDFViewer,
  Page,
  Text,
  View,
  Document,
} from "@react-pdf/renderer";
import moment from "moment";
import axios from "axios";
import config from "../../../config";
import PrintHeaderAccount from "../../../Pdf/PrintHeaderAccount";
import { Spin } from "antd";
import { saleReportStyles } from "../../../Pdf/pdfStyles";
import PrintFooter from "../../../Pdf/PrintFooter";
import {
  PrintFormateNumber,
  parseReportAmount,
} from "../../../Pdf/PrintFormateNumber";

const COL = {
  sr: 26,
  dated: 52,
  voucherNo: 40,
  debit: 110,
  credit: 110,
  description: 171,
  amount: 58,
};

const TABLE_WIDTH =
  COL.sr +
  COL.dated +
  COL.voucherNo +
  COL.debit +
  COL.credit +
  COL.description +
  COL.amount;

const parseFilterId = (value) => {
  const id = parseInt(value, 10);
  return Number.isFinite(id) && id > 0 ? id : 0;
};

const VOUCHER_MODE_LABELS = {
  CreditSale: "Credit Sale",
  CreditPurchase: "Credit Purchase",
  Payments: "Payments",
  Receipts: "Receipt",
  Expenses: "Expense",
  General: "General",
};

const DEBIT_COLUMN_LABELS = {
  CreditSale: "Customer name",
  CreditPurchase: "Customer name",
  Payments: "Paid To",
  Receipts: "Receipt In",
  Expenses: "Expense Account",
  General: "Debit Account",
};

const CREDIT_COLUMN_LABELS = {
  CreditSale: "From Account",
  CreditPurchase: "From Account",
  Payments: "From Account",
  Receipts: "From",
  Expenses: "From Account",
  General: "Credit Account",
};

const PrintVouchersDetail = () => {
  const urlParams = new URLSearchParams(window.location.search);

  const UserID = localStorage.getItem("ID");
  const AccessKey = localStorage.getItem("AccessKey");

  const StartDate = urlParams.get("StartDate");
  const EndDate = urlParams.get("EndDate");
  const AccountID = parseFilterId(urlParams.get("AccountID"));
  const VoucherType = urlParams.get("VoucherType") || "";
  const VoucherMode = urlParams.get("VoucherMode") || "General";
  const DebitAccountID = parseFilterId(
    urlParams.get("Debit_Account_ID") ?? urlParams.get("DebitAccountID")
  );
  const CreditAccountID = parseFilterId(
    urlParams.get("Credit_Account_ID") ?? urlParams.get("creditAccountID")
  );
  const OrderBy = urlParams.get("OrderBy") || "1";

  const [listofVouchers, setListofVouchers] = useState([]);
  const [reportMeta, setReportMeta] = useState({});
  const [loading, setLoading] = useState(true);

  const modeLabel = VOUCHER_MODE_LABELS[VoucherMode] || VoucherMode || "General";
  const debitColLabel = DEBIT_COLUMN_LABELS[VoucherMode] || "Debit Account";
  const creditColLabel = CREDIT_COLUMN_LABELS[VoucherMode] || "Credit Account";

  const periodLabel = useMemo(() => {
    const hasStart = StartDate && moment(StartDate).isValid();
    const hasEnd = EndDate && moment(EndDate).isValid();
    if (!hasStart && !hasEnd) {
      return "All dates";
    }
    const from = hasStart
      ? moment(StartDate).format(config.date_format)
      : "—";
    const to = hasEnd ? moment(EndDate).format(config.date_format) : "—";
    return `${from} to ${to}`;
  }, [StartDate, EndDate]);

  useEffect(() => {
    document.title = "Print Vouchers Details";
    if (!UserID || !AccessKey) {
      setLoading(false);
      return;
    }

    // Empty dates match listing pages: API defaults to full range (from 2022).
    const normalizedStart =
      StartDate && moment(StartDate).isValid()
        ? moment(StartDate).format("YYYY-MM-DD")
        : "";
    const normalizedEnd =
      EndDate && moment(EndDate).isValid()
        ? moment(EndDate).format("YYYY-MM-DD")
        : "";

    setLoading(true);
    axios({
      method: "post",
      url: config.base_url + "Voucher/GetVouchersWithType",
      headers: { "Content-Type": "application/json" },
      data: {
        UserID,
        AccessKey,
        StartDate: normalizedStart,
        EndDate: normalizedEnd,
        OrderBy,
        AccountID,
        VoucherType,
        VoucherMode,
        Debit_Account_ID: DebitAccountID,
        Credit_Account_ID: CreditAccountID,
        PageNo: 1,
        PageSize: 20000,
      },
    })
      .then(function (response) {
        if (response.data.status_code == 1) {
          setReportMeta(response.data);
          setListofVouchers(response.data.listofVouchers || []);
        } else {
          setReportMeta({});
          setListofVouchers([]);
        }
      })
      .catch(function () {
        setReportMeta({});
        setListofVouchers([]);
      })
      .finally(function () {
        setLoading(false);
      });
  }, [
    UserID,
    AccessKey,
    StartDate,
    EndDate,
    OrderBy,
    AccountID,
    VoucherType,
    VoucherMode,
    DebitAccountID,
    CreditAccountID,
  ]);

  const fmt = (val) => PrintFormateNumber(parseReportAmount(val), 0);

  const generateData = () => {
    if (!listofVouchers.length) return null;

    return listofVouchers.map((item, index) => (
      <View key={item.ID || index} style={saleReportStyles.row}>
        <View style={[saleReportStyles.td, { width: COL.sr }]}>
          <Text style={saleReportStyles.tdValue}>{index + 1}</Text>
        </View>
        <View style={[saleReportStyles.td, { width: COL.dated }]}>
          <Text style={saleReportStyles.tdValue}>
            {item.Dated
              ? moment(item.Dated).format(config.date_format)
              : "—"}
          </Text>
        </View>
        <View style={[saleReportStyles.td, { width: COL.voucherNo }]}>
          <Text style={saleReportStyles.tdValue}>{item.VoucherNo ?? "—"}</Text>
        </View>
        <View style={[saleReportStyles.td, { width: COL.debit }]}>
          <Text style={saleReportStyles.tdValue}>
            {item.Debit_Account?.Name || "—"}
          </Text>
        </View>
        <View style={[saleReportStyles.td, { width: COL.credit }]}>
          <Text style={saleReportStyles.tdValue}>
            {item.Credit_Account?.Name || "—"}
          </Text>
        </View>
        <View style={[saleReportStyles.td, { width: COL.description }]}>
          <Text style={saleReportStyles.tdValue}>
            {item.Description || "—"}
          </Text>
        </View>
        <View
          style={[
            saleReportStyles.td,
            { width: COL.amount, textAlign: "right" },
          ]}
        >
          <Text style={saleReportStyles.tdValue}>{fmt(item.Amount)}</Text>
        </View>
      </View>
    ));
  };

  const MyDocument = () => (
    <Document>
      <Page size="A4" style={saleReportStyles.page}>
        <PrintHeaderAccount compact />
        <View style={saleReportStyles.section}>
          <View style={saleReportStyles.titleBlock}>
            <Text style={saleReportStyles.pagetitle}>
              {modeLabel} Voucher Details
            </Text>
            <Text style={saleReportStyles.reportSubtitle}>
              Period: {periodLabel}
            </Text>
            {VoucherType ? (
              <Text style={saleReportStyles.reportSubtitle}>
                Voucher type: {VoucherType}
              </Text>
            ) : null}
            {reportMeta.TotalVouchers != null ? (
              <Text style={saleReportStyles.reportSubtitle}>
                Vouchers: {reportMeta.TotalVouchers}
              </Text>
            ) : null}
          </View>

          <View style={[saleReportStyles.table, { width: TABLE_WIDTH }]}>
            <View style={saleReportStyles.tableHead}>
              <View style={[saleReportStyles.th, { width: COL.sr }]}>
                <Text style={saleReportStyles.thValue}>Sr. No.</Text>
              </View>
              <View style={[saleReportStyles.th, { width: COL.dated }]}>
                <Text style={saleReportStyles.thValue}>Dated</Text>
              </View>
              <View style={[saleReportStyles.th, { width: COL.voucherNo }]}>
                <Text style={saleReportStyles.thValue}>No.</Text>
              </View>
              <View style={[saleReportStyles.th, { width: COL.debit }]}>
                <Text style={saleReportStyles.thValue}>{debitColLabel}</Text>
              </View>
              <View style={[saleReportStyles.th, { width: COL.credit }]}>
                <Text style={saleReportStyles.thValue}>{creditColLabel}</Text>
              </View>
              <View style={[saleReportStyles.th, { width: COL.description }]}>
                <Text style={saleReportStyles.thValue}>Description</Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: COL.amount, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>Amount</Text>
              </View>
            </View>

            {generateData()}

            <View style={saleReportStyles.totalsRow}>
              <View
                style={[
                  saleReportStyles.th,
                  {
                    width: TABLE_WIDTH - COL.amount,
                    textAlign: "right",
                  },
                ]}
              >
                <Text style={saleReportStyles.thValue}>Total</Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: COL.amount, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>
                  {fmt(reportMeta.Total)}
                </Text>
              </View>
            </View>
          </View>
        </View>
        <PrintFooter fixed />
      </Page>
    </Document>
  );

  return (
    <div style={{ width: "100%", height: "100vh", overflow: "hidden" }}>
      {loading ? (
        <div
          style={{
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Spin />
        </div>
      ) : (
        <PDFViewer width="100%" height="100%">
          <MyDocument />
        </PDFViewer>
      )}
    </div>
  );
};

export default PrintVouchersDetail;
